import each from 'lodash/each';
import type { ComputedRef, Ref, WritableComputedRef } from 'vue';
import { computed, nextTick, ref } from 'vue';
import type { AnySchema } from 'yup';
import { ValidationError } from 'yup';

import type { CLIENT_EDITOR_SCOPE } from '@/components/modals/ClientEditor/types';
import type { ClientResource, Role, ROLE } from '@/features/Clients';
import { OPTIONAL_ROLE, useClientStore } from '@/features/Clients';
import { VALIDATION_MAP_KEYS } from '@/features/validation/validationMapKeys';
import { VGL_ERROR_CODE } from '@/services/errorHandler/errorCodes';
import { getErrorMessage } from '@/services/errorHandler/utils';
import { VglError } from '@/services/errorHandler/vglError';
import { useForm } from '@/utils/form/useForm';
import { useSectionNavigator } from '@/utils/useSectionNavigator/useSectionNavigator';
import hashCode from '@/utils/hashCode/hashCode';

type AnyComputedRef =
  | ComputedRef<ClientResource | undefined>
  | WritableComputedRef<ClientResource | undefined>
  | Ref<ClientResource | undefined>;

interface ValidationStateObj {
  errors: Ref<Record<string, string[]>>;
  validateFunctions: Map<string, () => void>;
}

interface UseValidation {
  errors: Ref<Record<string, string[]>>;
  validate: () => void;
  validateAt: (path: string) => void;

  addError: (fieldPath: string, error: string, role?: ROLE) => void;
  deleteError: (fieldPath: string, role?: ROLE) => void;
  deleteErrorsByRole: (role: Role) => void;

  createClientValidator: (
    state: AnyComputedRef,
    schema: AnySchema,
    role: Role,
  ) => (path: string) => void;

  schemaHash?: string;
  removeValidationByHash: (usedHash: string) => void;
  removeClientValidatorByRole: (role: Role) => void;
}

export const getErrorPath = (path: string, role?: Role): string => {
  const prefix = role !== undefined ? `${role}-` : '';
  return `${prefix}${path}`;
};

export const parseInnerErrors = (
  error: ValidationError,
  role?: Role,
): Record<string, string[]> => {
  const validationErrors: Record<string, string[]> = {};

  // for single field validation we only get one error without inner errors
  // so we just return that on error.
  if (error.inner.length === 0) {
    if (error.path !== undefined) {
      validationErrors[getErrorPath(error.path, role)] = error.errors;
    }
    return validationErrors;
  }

  each(error.inner, (err) => {
    // if path is undefined it could mean we need to watch error.inner
    // for more errors as current error is just an wrapping object.
    // This could be addressed with a recursive call of this function.
    // But currently that's not needed.
    if (err.path !== undefined) {
      validationErrors[getErrorPath(err.path, role)] = err.errors;
    }
  });

  return validationErrors;
};

const validationStateMap = new Map<string, ValidationStateObj>();

export const useValidation = <State>(
  key: VALIDATION_MAP_KEYS | CLIENT_EDITOR_SCOPE,
  schema?: AnySchema | (() => AnySchema), // use function callbacks for schemas which will be updated after initiating the validation
  state?: State,
): UseValidation => {
  let vSchema: (() => AnySchema) | undefined;

  if (schema !== undefined) {
    vSchema = typeof schema === 'function' ? schema : () => schema;
  }

  if (!validationStateMap.has(key)) {
    validationStateMap.set(key, {
      errors: ref({}),
      validateFunctions: new Map(),
    });
  }

  const validationStateObj = validationStateMap.get(key);

  if (validationStateObj === undefined) {
    throw new VglError(VGL_ERROR_CODE.UTIL_CREATE_VALIDATE_STATE_FAILED, {
      additionalErrorMessage: `StateKey: ${key}`,
    });
  }

  const { errors } = validationStateObj;
  const validateAllFuncs = validationStateObj.validateFunctions;

  const createValidateAllFunc =
    (
      validationState: ComputedRef<unknown> | AnyComputedRef,
      validationSchema: () => AnySchema,
      role?: Role,
    ) =>
    () => {
      try {
        if (
          role === OPTIONAL_ROLE.ABWEICHENDER_HALTER &&
          key === VALIDATION_MAP_KEYS.EVB_MODAL &&
          useClientStore().getDataByRole(role) === undefined
        ) {
          return;
        }

        validationSchema().validateSync(validationState.value, {
          abortEarly: false,
          context: state,
        });
      } catch (error) {
        if (error instanceof ValidationError) {
          errors.value = {
            ...errors.value,
            ...parseInnerErrors(error, role),
          };
        } else {
          throw new VglError(VGL_ERROR_CODE.UTIL_VALIDATE_ALL_FAILED, {
            additionalErrorMessage: `Error: ${getErrorMessage(
              error,
            )} - State: ${JSON.stringify(validationSchema())}`,
            cause: error,
          });
        }
      }
    };

  const validateAt = (path: string): void => {
    try {
      if (vSchema !== undefined) {
        vSchema().validateSyncAt(path, state, { context: state });
      }
      delete errors.value[path];
    } catch (error) {
      if (error instanceof ValidationError) {
        errors.value = {
          ...errors.value,
          ...parseInnerErrors(error),
        };
      } else {
        throw new VglError(VGL_ERROR_CODE.UTIL_VALIDATE_AT_FAILED, {
          additionalErrorMessage: `Error: ${getErrorMessage(
            error,
          )} - Path: ${path}`,
          cause: error,
        });
      }
    }
  };

  const createClientValidator = (
    clientState: AnyComputedRef,
    clientSchema: AnySchema,
    role: Role,
  ): ((path: string) => void) => {
    validateAllFuncs.set(
      role,
      createValidateAllFunc(clientState, () => clientSchema, role),
    );

    return (path: string) => {
      const errorPath = getErrorPath(path, role);

      try {
        clientSchema.validateSyncAt(path, clientState.value);
        delete errors.value[errorPath];
      } catch (error) {
        if (error instanceof ValidationError) {
          errors.value = {
            ...errors.value,
            ...parseInnerErrors(error, role),
          };
        } else {
          throw new VglError(VGL_ERROR_CODE.UTIL_CREATE_VALIDATOR_FAILED, {
            additionalErrorMessage: `Error: ${getErrorMessage(
              error,
            )} - Path: ${path} - Schema: ${JSON.stringify(clientSchema)}`,
            cause: error,
          });
        }
      }
    };
  };

  let schemaHash;
  if (vSchema !== undefined) {
    schemaHash = key + hashCode(vSchema());
    validateAllFuncs.set(
      schemaHash,
      createValidateAllFunc(
        computed(() => state),
        vSchema,
      ),
    );
  }

  const validate = (): void => {
    errors.value = {};
    validateAllFuncs.forEach((func) => func());

    if (Object.keys(errors.value).length > 0) {
      useForm().scrollToFirstError(errors.value);
      nextTick(() => useSectionNavigator().showErrors(errors.value));
      throw new ValidationError('input validation failed');
    }
  };

  const addError = (fieldPath: string, error: string, role?: ROLE): void => {
    errors.value[getErrorPath(fieldPath, role)] = [error];
  };

  const deleteError = (fieldPath: string, role?: ROLE): void => {
    delete errors.value[getErrorPath(fieldPath, role)];
  };

  const deleteErrorsByRole = (role: Role): void => {
    Object.keys(errors.value).forEach((errorPath) => {
      if (errorPath.startsWith(`${role}-`)) {
        delete errors.value[errorPath];
      }
    });
  };

  const removeValidationByHash = (usedHash: string): void => {
    validateAllFuncs.delete(usedHash);
  };

  const removeClientValidatorByRole = (role: Role) => {
    validateAllFuncs.delete(role);
  };

  return {
    errors,
    validate,
    validateAt,
    createClientValidator,
    addError,
    deleteError,
    deleteErrorsByRole,
    schemaHash,
    removeValidationByHash,
    removeClientValidatorByRole,
  };
};
